import { computed, observable, runInAction } from "mobx"

import { batchInfo, emptyBatch } from "@/lib/apiClient/client.ts"
import { withGlobalLoader } from "@/lib/context.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"

import type { Pair } from "@/lib/client/api-types.ts"
import type { AiAgentTask, Batch, PR_Project2, RuntimeConfig } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { IObservableValue } from "mobx"

const log = getLogger("ProjectPreviewSetupViewModel")

export class ProjectPreviewSetupViewModel extends Model {
  readonly error = observable.box<string | null>(null)

  readonly runtimeConfig = observable.box<Pair<RuntimeConfig, boolean> | null>(null)

  readonly tasksBatch = observable.box<Batch<AiAgentTask>>(emptyBatch())
  readonly tasks = computed(() => this.tasksBatch.get().data)

  readonly isReadonly = computed(() => !!this.runtimeConfig.get()?.second)
  readonly setupInProgress = observable.box<boolean>(false)

  readonly runtimeConfigPresent = computed(() => !!this.runtimeConfig.get())

  constructor(
    readonly context: OrgContext,
    readonly project: PR_Project2,
    readonly canEditProject: IObservableValue<boolean>
  ) {
    super("ProjectPreviewSetupViewModel")

    void this.load()
  }

  get projectKey() {
    return this.project.key
  }

  get repository() {
    return this.project.repository.name
  }

  async load() {
    runInAction(() => {
      this.isLoading.set(true)
      this.error.set(null)
    })

    try {
      const [configAndReadonly, setupTasks] = await withGlobalLoader(this.context, Promise.all([
        this.context.queries.getProjectRuntimeConfig(this.projectKey, this.repository),
        this.context.queries.listPreviewSetupTasks(
          this.projectKey,
          this.repository,
          batchInfo(20)
        )
      ]))

      runInAction(() => {
        this.runtimeConfig.set(configAndReadonly)
        this.tasksBatch.set(setupTasks)
        this.isLoading.set(false)
      })
    } catch (error) {
      log.error("Failed to load preview configuration:", error)
      runInAction(() => {
        this.error.set(`Failed to load preview configuration: ${error}`)
        this.isLoading.set(false)
      })
    }
  }

  get warmupScript(): string | null {
    return this.runtimeConfig.get()?.first?.beforeCommand ?? null
  }

  get previewScripts(): RuntimeConfig.RunAppCommand[] {
    return this.runtimeConfig.get()?.first?.previewCommands ?? []
  }

  readonly setupAgain = (): Promise<AiAgentTask> => {
    runInAction(() => this.setupInProgress.set(true))
    try {
      return withGlobalLoader(this.context, this.context.queries.apiClient.configurePreviewRuntime(this.projectKey, this.repository))
    } catch (err) {
      log.error("Failed to trigger preview configuration:", err)
      throw err
    } finally {
      runInAction(() => this.setupInProgress.set(false))
    }
  }
}